// ── Card Brands ────────────────────────────────────────────────────────────

export type CardBrand = "visa" | "mastercard" | "amex" | "discover" | "diners" | "jcb" | "unionpay" | "unknown";

const VALID_BRANDS: Set<string> = new Set(["visa", "mastercard", "amex", "discover", "diners", "jcb", "unionpay"]);

export function toCardBrand(brand: string | null | undefined): CardBrand {
  return brand && VALID_BRANDS.has(brand) ? (brand as CardBrand) : "unknown";
}

// ── Payment Methods ────────────────────────────────────────────────────────

export interface PaymentMethodCard {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  country: string | null;
  funding: string | null;
  fingerprint: string | null;
}

export interface PaymentMethodAddress {
  city: string | null;
  country: string | null;
  line1: string | null;
  line2: string | null;
  postalCode: string | null;
  state: string | null;
}

export interface PaymentMethodBillingDetails {
  name: string | null;
  address: PaymentMethodAddress | null;
}

export interface ActiveWorkflow {
  workflowId: number;
  workflowName: string;
}

export type CardSource = "team" | "org";

export interface PaymentMethod {
  id: number;
  type: string;
  createdAt: string;
  card: PaymentMethodCard;
  billingDetails: PaymentMethodBillingDetails;
  isPrimary: boolean;
  isExpired: boolean;
  status: string;
  activeWorkflows: ActiveWorkflow[];
  /** Master Account team list only: an organization card shared to this team. */
  source?: CardSource;
  addedBy?: { id: number | null; name: string | null };
}

// ── Master Account team cards ──────────────────────────────────────────────

export interface TeamCardsResponse {
  cards: PaymentMethod[];
  maxCards: number;
  canAddMore: boolean;
  success: boolean;
}

export interface AddTeamCardInput {
  stripePaymentMethodId: string;
  setAsPrimary?: boolean;
  sharedWithTeam?: boolean;
}

// ── Onboard ────────────────────────────────────────────────────────────────

export interface BillingAddressFormData {
  fullName: string;
  phoneNumber?: string;
  country: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  zipCode: string;
  nickname?: string;
}

export interface OnboardResponse {
  savedPaymentMethods: PaymentMethod[];
  defaultBillingAddress: BillingAddressFormData | null;
  maxCards: number;
  canAddMore: boolean;
  success: boolean;
}

// ── Charges ────────────────────────────────────────────────────────────────

export interface CostBreakdown {
  subtotal: number;
  processingFee: number;
  tax: number;
  discount: number;
  total: number;
  currency: string;
  feePercentage?: number;
  feeFlatFeeCents?: number;
}

export interface InitiateChargeRequest {
  entityId: string;
  entityType: "CAMPAIGN";
  paymentMethodId: number | null;
  saveCard: boolean;
  /** Pre-tax, pre-fee subtotal in the smallest currency unit (cents). */
  subtotalCents: number;
  /** ISO-4217 currency code. Backend asserts it matches billing-profile country default. */
  currency: string;
  /** Optional Stripe promotion code applied to the charge. */
  couponCode?: string;
  /**
   * HUB-498: apply the client's active review-discount entitlement (resolved
   * server-side). Wins over `couponCode` — one discount per order.
   */
  applyReviewDiscount?: boolean;
}

export interface InitiateChargeResponse {
  clientSecret: string;
  chargeUuid: string;
  success: boolean;
}

export interface VerifyChargeResponse {
  status: "authorized" | "paid" | "failed" | "pending";
  chargeUuid: string;
}

// ── Server-Authoritative Breakdown ─────────────────────────────────────────
// Source of truth: oppizi-api/src/billing/application/services/BreakdownCalculator.ts.
// Local `CostBreakdown` (in dollars) is preserved for CheckoutSummary display;
// `breakdownResultToCostBreakdown` bridges the cents-based API shape into it.

export type TaxProvider = "taxjar" | "xero" | "none";

export type TaxDisclaimer = "TAX_NOT_INCLUDED";

export interface TaxLineItem {
  postalCode: string;
  taxCents: number;
  ratePercentage: number;
}

export interface BreakdownTaxDetails {
  provider: TaxProvider;
  ratePercentage: number;
  taxType: string | null;
  taxRateName: string | null;
  breakdown: TaxLineItem[];
  disclaimer: TaxDisclaimer | null;
}

export interface BreakdownProcessingFeeDetails {
  percentage: number;
  flatFeeCents: number;
  supported: boolean;
}

export interface AppliedCoupon {
  promotionCodeId: string;
  couponId: string;
  code: string;
  amountOffCents: number | null;
  percentOff: number | null;
}

export interface BreakdownResult {
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  processingFeeCents: number;
  totalCents: number;
  currency: string;
  appliedCoupon: AppliedCoupon | null;
  tax: BreakdownTaxDetails;
  processingFee: BreakdownProcessingFeeDetails;
}

export interface PreviewBreakdownRequest {
  subtotalCents: number;
  currency?: string;
  entityId?: string;
  entityType?: "CAMPAIGN";
  couponCode?: string;
  /** HUB-498: apply the client's active review discount (server-resolved). */
  applyReviewDiscount?: boolean;
}

export interface PreviewBreakdownResponse {
  success: boolean;
  breakdown: BreakdownResult;
}

/**
 * Converts the cents-based API breakdown into the dollars-based local
 * CostBreakdown that CheckoutSummary already consumes. Bridge the two
 * shapes without refactoring the drawer.
 */
export function breakdownResultToCostBreakdown(b: BreakdownResult): CostBreakdown {
  return {
    subtotal: b.subtotalCents / 100,
    processingFee: b.processingFeeCents / 100,
    tax: b.taxCents / 100,
    discount: b.discountCents / 100,
    total: b.totalCents / 100,
    currency: b.currency,
    feePercentage: b.processingFee.percentage,
    feeFlatFeeCents: b.processingFee.flatFeeCents,
  };
}

// ── Card Input ─────────────────────────────────────────────────────────────

export interface CardFieldErrors {
  cardholderName?: string;
  cardNumber?: string;
  expiry?: string;
  cvc?: string;
}

// ── Country Address Config (ported from platform-next / dynamic-location-form-poc) ──

export type RegionFieldType = "dropdown" | "text" | "hidden";

export interface RegionOption {
  code: string;
  name: string;
}

export interface CountryAddressConfig {
  regionFieldType: RegionFieldType;
  regionLabel: string;
  regionRequired: boolean;
  regionOptions?: RegionOption[];
  postalLabel: string;
  postalRequired: boolean;
  postalRegex?: string;
}

// ── Billing Profile (Master Account) ──

export interface BillingProfileContactInput {
  fullName: string;
  email: string;
  isPrimary?: boolean;
}

export interface CreateBillingProfileInput {
  legalName: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateRegion?: string | null;
  postalCode?: string | null;
  country?: string | null;
  billingEmail?: string | null;
  contacts?: BillingProfileContactInput[];
}
